import React, { useState } from 'react';

import { Col, Dropdown, Modal, Row } from 'react-bootstrap';
import '../JobStageSummary.scss';
import { CustomButton } from '../../../_common/CustomButton/CustomButton';
import { useMutation, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { GET_JOBLOGS_BY_JOBID, GET_WELL_HEADS_BY_STAGE_GROUP_ID } from '../../../../graphql/client/queries';
import {
    GetWellheadsByStageGroupId,
    GetWellheadsByStageGroupId_getWellheadsByStageGroupId as StageWellheads,
    GetJobLogsDetails,
    GetJobLogsDetails_getJobLogsDetails as JobLogDetailsType,
    GetJobLogMetaData_getJobLogMetaData_types as JobLogTypesType,
    GetJobLogMetaData_getJobLogMetaData_issues as JobLogIssueType,
    GetStages_getStages as StageNumberType,
    GetJobLogMetaData_getJobLogMetaData_rootcauses as JobLogRootCauseType,
    GetJobLogMetaData_getJobLogMetaData_locationLOFAs as JobLogLocationLofaType,
} from '../../../../types/schemaTypes';
import { Loading } from '../../../_common/Loading/Loading';
import { ADD_EDIT_JOB_LOGS } from '../../../../graphql/client/mutations';
import { PermissionEnum } from '../../../../types/PermissionEnum';
import { useAuthContext } from '../../../Auth/AuthProvider';

interface ResumeStageSummaryModalProps {
    showResumeStageModal: boolean;
    stageGroupId: number | undefined;
    jobId: number;
    jobLogRootCauses: JobLogRootCauseType[];
    jobLogLocationLofa: JobLogLocationLofaType[];
    handleOnClose: (modalType) => void;
    handleOnSave: (jobStageIds: any) => void;
    mutationLoading: boolean;
    modalError: string | undefined;
    setModalError: React.Dispatch<React.SetStateAction<string>>;
}

const ResumeStageSummaryModal = ({
    showResumeStageModal,
    stageGroupId,
    jobLogRootCauses,
    jobLogLocationLofa,
    handleOnClose,
    jobId,
    handleOnSave,
    mutationLoading,
    modalError,
    setModalError,
}: ResumeStageSummaryModalProps): React.ReactElement => {
    const authContext = useAuthContext();
    const isAddJobLogsEnabled = authContext.userHasPermission(PermissionEnum.AddJobLogs);

    const [logCount, setLogCount] = useState(0);

    const [wellheadStage, setWellheadStage] = useState<string>();
    const [jobStageIds, setJobStageIds] = useState<number[]>();

    const [logId, setLogId] = useState<number | null>(-1);
    const [logType, setLogType] = useState<string | null>('');
    const [logTypeId, setLogTypeId] = useState<number | null>(null);
    const [issueType, setIssueType] = useState<string | null>('');
    const [issueTypeId, setIssueTypeId] = useState<number | null>(null);
    const [jobStageId, setJobStageId] = useState<number | null>();
    const [stage, setStage] = useState<string | null>();
    const [downtime, setDowntime] = useState<number | null>(null);
    const [loadId, setLoadId] = useState<number | null>(null);
    const [lofa, setLofa] = useState<string | null>('');
    const [lofaId, setLofaId] = useState<number | null>(null);
    const [amount, setAmount] = useState<number | null>(null);
    const [rootCause, setRootCause] = useState<string | null>('');
    const [rootCauseId, setRootCauseId] = useState<number | null>(null);
    const [notes, setNotes] = useState<string | null>('');
    const [correctiveAction, setCorrectiveAction] = useState<string | null>('');

    const [downTimeError, setDownTimeError] = useState('');
    const [loadIdError, setLoadIdError] = useState('');
    const [amountError, setAmountError] = useState('');
    const [notesError, setNotesError] = useState('');

    const { loading, error, data } = useQuery<GetWellheadsByStageGroupId>(GET_WELL_HEADS_BY_STAGE_GROUP_ID, {
        variables: {
            stageGroupId: stageGroupId,
        },
        fetchPolicy: 'network-only',
    });
    const wellheadStageList: StageWellheads[] = (data?.getWellheadsByStageGroupId ?? []).filter(
        (wellHead): wellHead is StageWellheads => wellHead != null && wellHead.status === 'Paused',
    );

    const { loading: jobLogsLoading, error: jobLogsError, data: jobLogsData } = useQuery<GetJobLogsDetails>(
        GET_JOBLOGS_BY_JOBID,
        {
            variables: {
                jobId: jobId,
                tzinfo: encodeURIComponent(
                    new Date()
                        .toString()
                        .split('(')[1]
                        .replace(')', ''),
                ),
            },
            fetchPolicy: 'network-only',
        },
    );

    const jobLogs: JobLogDetailsType[] = (jobLogsData?.getJobLogsDetails ?? []).filter(
        (jobLog): jobLog is JobLogDetailsType => jobLog != null,
    );
    if (logId === -1 && jobLogs.length > 0) {
        const log = jobLogs[0];
        setLogId(log.id);
        setLogType(log.jobLogType);
        setLogTypeId(log.jobLogTypeId);
        setIssueType(log.jobLogIssue);
        setIssueTypeId(log.jobLogIssueId);
        setDowntime(log.estimatedDowntime);
        setJobStageId(log.jobStageId);
        setStage(log.stage);
        setLoadId(log.loadId);
        setLofa(log.locationLofa);
        setAmount(log.amount);
        setRootCause(log.rootCause);
        setRootCauseId(log.rootCauseId);
        setNotes(log.notes);
        setCorrectiveAction(log.correctiveAction);
        setLogCount(1);
    }

    const [addEditJobLog, { loading: addEditJobLogLoading }] = useMutation(ADD_EDIT_JOB_LOGS, {
        onError: error => {
            if (CombinedGraphQLErrors.is(error)) {
                setModalError(error.errors[0]?.message?.toString().replace(',', ' ') || error.message);
            } else {
                setModalError(error.message ?? error);
            }
        },
    });

    const handleOnChangeWellheadStage = (wellhead: string, stageNumber: number, jobStageId: number) => {
        if (wellhead === 'ALL') {
            const tempJobStageIds: number[] = [];
            let jobStageIds = '';
            wellheadStageList.map((WellheadStageItem, index) => {
                tempJobStageIds.push(WellheadStageItem.jobStageId);
                if (index === 0) jobStageIds = WellheadStageItem.jobStageId.toString();
                else jobStageIds += ',' + WellheadStageItem.jobStageId.toString();
            });
            setWellheadStage('Select All');
            setJobStageIds(tempJobStageIds);
        } else {
            setWellheadStage(`${wellhead} - Stage ${stageNumber}`);
            setJobStageIds([jobStageId]);
        }
    };

    const handleOnChangeDowntime = event => {
        setDownTimeError('');
        if (Number.isNaN(event.target.value)) setDowntime(null);
        else setDowntime(parseInt(event.target.value));
    };

    const handleOnChangeLoadId = event => {
        setLoadIdError('');
        if (Number.isNaN(event.target.value)) setLoadId(null);
        else setLoadId(parseInt(event.target.value));
    };

    const handleOnChangeLofa = (lofaId, lofa) => {
        setLofaId(lofaId);
        setLofa(lofa);
    };

    const handleOnChangeAmount = event => {
        setAmountError('');
        if (Number.isNaN(event.target.value)) setAmount(null);
        else setAmount(parseInt(event.target.value));
    };

    const handleOnChangeRootCause = (rootCauseId, rootCause) => {
        setRootCauseId(rootCauseId);
        setRootCause(rootCause);
    };

    const handleOnChangeCorrectiveAction = event => {
        setCorrectiveAction(event.target.value);
    };

    const handleOnChangeNotes = event => {
        setNotesError('');
        setNotes(event.target.value);
    };

    const onClickIgnore = () => {
        if (logCount !== jobLogs.length) {
            const log = jobLogs[logCount];
            setLogId(log.id);
            setLogType(log.jobLogType);
            setLogTypeId(log.jobLogTypeId);
            setIssueType(log.jobLogIssue);
            setIssueTypeId(log.jobLogIssueId);
            setDowntime(log.estimatedDowntime);
            setJobStageId(log.jobStageId);
            setStage(log.stage);
            setLoadId(log.loadId);
            setLofa(log.locationLofa);
            setAmount(log.amount);
            setRootCause(log.rootCause);
            setRootCauseId(log.rootCauseId);
            setNotes(log.notes);
            setCorrectiveAction(log.correctiveAction);
        }
        setLogCount(logCount + 1);
    };

    const onClickResolve = () => {
        let isSaveEnabled = true;

        if (downtime && downtime < 0) {
            isSaveEnabled = false;
            setDownTimeError('Enter a vaild number');
        }

        if (loadId && loadId < 0) {
            isSaveEnabled = false;
            setLoadIdError('Enter a vaild Load Id');
        }

        if (amount && amount < 0) {
            isSaveEnabled = false;
            setAmountError('Enter a vaild Amount');
        }

        if (notes && notes.length > 2000) {
            isSaveEnabled = false;
            setNotesError('Notes cannot be more than 2000 character');
        }

        if (isSaveEnabled) {
            addEditJobLog({
                variables: {
                    input: {
                        jobLogId: logId,
                        jobLogIssueId: issueTypeId,
                        jobLogTypeId: logTypeId,
                        jobId: jobId,
                        startDate: null,
                        endDate: new Date(),
                        estimatedDowntime: downtime,
                        jobStageId: jobStageId,
                        loadId: loadId,
                        notes: notes,
                        locationLofa: lofa,
                        amount: amount,
                        rootCauseId: rootCauseId,
                        correctiveAction: correctiveAction,
                    },
                },
            });

            if (logCount !== jobLogs.length) {
                const log = jobLogs[logCount];
                setLogId(log.id);
                setLogType(log.jobLogType);
                setLogTypeId(log.jobLogTypeId);
                setIssueType(log.jobLogIssue);
                setIssueTypeId(log.jobLogIssueId);
                setDowntime(log.estimatedDowntime);
                setJobStageId(log.jobStageId);
                setStage(log.stage);
                setLoadId(log.loadId);
                setLofa(log.locationLofa);
                setAmount(log.amount);
                setRootCause(log.rootCause);
                setRootCauseId(log.rootCauseId);
                setNotes(log.notes);
                setCorrectiveAction(log.correctiveAction);
            }
            setLogCount(logCount + 1);
        }
    };

    const onClickSave = () => {
        handleOnSave(jobStageIds);
    };

    const setContinueButtonStatus = (): boolean => {
        if (
            jobStageIds?.length === 0 ||
            !logTypeId ||
            (logTypeId !== 6 && !issueTypeId) ||
            mutationLoading ||
            addEditJobLogLoading
        ) {
            return true;
        }
        if (modalError === undefined || modalError === '') {
            return false;
        } else {
            return true;
        }
    };

    const onClickClose = () => {
        handleOnClose('resumeStage');
    };

    if (wellheadStageList.length === 1 && !wellheadStage) {
        handleOnChangeWellheadStage(
            wellheadStageList[0].wellhead,
            wellheadStageList[0].stage,
            wellheadStageList[0].jobStageId,
        );
    }
    if (wellheadStageList.length > 1 && !wellheadStage) {
        handleOnChangeWellheadStage('ALL', 0, 0);
    }

    if (!jobLogsLoading && !addEditJobLogLoading && (jobLogs.length === 0 || logCount === jobLogs.length + 1)) {
        return (
            <Modal show={showResumeStageModal} onHide={onClickClose} dialogClassName="Pause-Modal-Container">
                {(mutationLoading || jobLogsLoading) && (
                    <span className="AddStageModal-Loading">
                        <Loading />
                    </span>
                )}
                <Modal.Header className="AddStageModal-Header">
                    <Modal.Title className="AddStageModal-Header-Container">
                        <button className="material-icons AddStageModal-X-Button" onClick={onClickClose}>
                            close
                        </button>
                        <div className="AddStageModal-Title">Resume Stage</div>
                        <div className="AddStageModal-Error">{modalError}</div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="Pause-Modal-Body">
                    <div>Select a Wellhead-Stage below and click continue to proceed</div>
                    <Row>
                        <Col md={5}>
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-dark"
                                    id="dropdown-basic"
                                    className="EndStage-wellheadStage"
                                >
                                    <span>{wellheadStage}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {wellheadStageList.length > 1 && (
                                        <Dropdown.Item 
                                       // onSelect={() => handleOnChangeWellheadStage('ALL', 0, 0)}
                                        
                                       onClick={() => handleOnChangeWellheadStage('ALL', 0, 0)}
                                       >
                                            Select All
                                        </Dropdown.Item>
                                    )}
                                    {wellheadStageList.map((wellheadStageItem, index) => (
                                        <Dropdown.Item
                                            key={index}
                                            // onSelect={() =>
                                            //     handleOnChangeWellheadStage(
                                            //         wellheadStageItem.wellhead,
                                            //         wellheadStageItem.stage,
                                            //         wellheadStageItem.jobStageId,
                                            //     )
                                            // }
                                            onClick={() =>
                                                handleOnChangeWellheadStage(
                                                    wellheadStageItem.wellhead,
                                                    wellheadStageItem.stage,
                                                    wellheadStageItem.jobStageId,
                                                )
                                            }
                                        >
                                            {wellheadStageItem.wellhead} - Stage {wellheadStageItem.stage}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="AddStageModal-Footer">
                    <CustomButton title="Cancel" isBordered onClick={onClickClose} />
                    <CustomButton title="Continue" onClick={() => onClickSave()} isDisabled={mutationLoading} />
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={showResumeStageModal} onHide={onClickClose} dialogClassName="Pause-Modal-Container">
            {(jobLogsLoading || mutationLoading || addEditJobLogLoading) && (
                <span className="AddStageModal-Loading">
                    <Loading />
                </span>
            )}

            <Modal.Header className="AddStageModal-Header">
                <Modal.Title className="AddStageModal-Header-Container">
                    <button className="material-icons AddStageModal-X-Button" onClick={onClickClose}>
                        close
                    </button>
                    <div className="AddStageModal-Title">Active Job Events</div>
                    <div className="AddStageModal-Error">{modalError}</div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="Pause-Modal-Body">
                <Row className="Pause-Modal-Row-Container">
                    <Col md={5}>
                        <label className="Pause-Modal-Label">*Job Log Type</label>
                        <Dropdown className="Pause-Modal-Dropdown">
                            <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter" disabled={true}>
                                <span>{logType === '' ? 'Job Log Type' : logType}</span>
                            </Dropdown.Toggle>
                        </Dropdown>
                    </Col>

                    {logTypeId !== 6 ? (
                        <Col md={5}>
                            <label className="Pause-Modal-Label">*Job Log Issue</label>
                            <Dropdown className="Pause-Modal-Dropdown">
                                <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter" disabled={true}>
                                    <span>{issueType === '' ? 'Issue' : issueType}</span>
                                </Dropdown.Toggle>
                            </Dropdown>
                        </Col>
                    ) : (
                        <></>
                    )}
                </Row>

                {[1, 2, 3, 6].includes(logTypeId ?? 0) ? (
                    <Row className="Pause-Modal-Row-Container">
                        <Col md={5}>
                            <div className="Pause-Modal-Input-Container">
                                <label className="Pause-Modal-Label">Estimated Downtime (in minutes)</label>
                                <input
                                    type="number"
                                    className="Pause-Modal-Input"
                                    value={downtime ?? ''}
                                    onChange={event => handleOnChangeDowntime(event)}
                                />
                                <span className="Pause-Modal-Error">{downTimeError}</span>
                            </div>
                        </Col>

                        {stage && (
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle
                                        variant="outline-dark"
                                        id="dropdown-basic"
                                        className="EndStage-wellheadStage"
                                        disabled={true}
                                    >
                                        <span>{stage}</span>
                                    </Dropdown.Toggle>
                                </Dropdown>
                            </Col>
                        )}
                    </Row>
                ) : (
                    <></>
                )}

                {logTypeId === 4 ? (
                    <>
                        <Row className="Pause-Modal-Row-Container">
                            <Col md={5}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Load ID</label>
                                    <input
                                        type="number"
                                        className="Pause-Modal-Input"
                                        value={loadId ?? ''}
                                        onChange={event => handleOnChangeLoadId(event)}
                                    />
                                    <span className="Pause-Modal-Error">{loadIdError}</span>
                                </div>
                            </Col>
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter">
                                        <span>{lofa === '' ? 'Lofa/ Location' : lofa}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleOnChangeLofa(null, '')}>None</Dropdown.Item>
                                        {jobLogLocationLofa.map((locationLofa, index) => (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() =>
                                                    handleOnChangeLofa(locationLofa.id, locationLofa.descrip)
                                                }
                                            >
                                                {locationLofa.descrip}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col>
                        </Row>

                        <Row className="Pause-Modal-Row-Container">
                            <Col md={5}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Amount</label>
                                    <input
                                        type="number"
                                        className="Pause-Modal-Input"
                                        value={amount ?? ''}
                                        onChange={event => handleOnChangeAmount(event)}
                                    />
                                    <span className="Pause-Modal-Error">{amountError}</span>
                                </div>
                            </Col>
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter">
                                        <span>{rootCause === '' ? 'Root Cause' : rootCause}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleOnChangeRootCause(null, '')}>
                                            None
                                        </Dropdown.Item>
                                        {jobLogRootCauses.map((rootCause, index) => (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() => handleOnChangeRootCause(rootCause.id, rootCause.descrip)}
                                            >
                                                {rootCause.descrip}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col>
                        </Row>

                        <Row className="Pause-Modal-Row-Container">
                            <Col md={10}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Corrective Action</label>
                                    <input
                                        type="text"
                                        className="Pause-Modal-Input"
                                        value={correctiveAction ?? ''}
                                        onChange={event => handleOnChangeCorrectiveAction(event)}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <></>
                )}

                <Row className="Pause-Modal-Row-Container">
                    <Col md={10}>
                        <div className="Pause-Modal-Input-Container">
                            <label className="Pause-Modal-Label">Notes</label>
                            <input
                                type="text"
                                className="Pause-Modal-Input"
                                value={notes ?? ''}
                                onChange={event => handleOnChangeNotes(event)}
                            />
                            <span className="Pause-Modal-Error">{notesError}</span>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className="AddStageModal-Footer">
                <CustomButton title="Ignore" isBordered onClick={() => onClickIgnore()} />
                {isAddJobLogsEnabled ? (
                    <CustomButton
                        title="Resolve"
                        onClick={() => onClickResolve()}
                        isDisabled={setContinueButtonStatus()}
                    />
                ) : (
                    <CustomButton title="Cancel" isBordered onClick={onClickClose} />
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ResumeStageSummaryModal;
